<template>
  <div class="col-lg-3 mb-4 col-6 product-grid no-overflow">
    <div class="product-wrapper text-center">
      <div class="product-img px-4 py-3 position-relative">
        <add-to-wishlist v-if="isLoggedIn" :prod="product" class="fr-wish"></add-to-wishlist>
        <template v-if="deliveryDate != ''">
          <router-link :to="`/${product.slug}?date=${deliveryDate}`">
            <div class="ribbon" v-if="product.highlight_id">
              <span
                :style="`border-color: ${product.highlight_hex_code}; background: ${product.highlight_hex_code}; background: linear-gradient(${ product.highlight_hex_code } 0%, ${ product.highlight_hex_code } 100%);`">{{
                product.highlight_name }}</span>
            </div>
            <img v-if="product.image" :src="product.image" class="" :alt="product.name" />
            <img v-else :src="`${$imagePath}` + getDefaultImage(product.product_type_id)" class="" :alt="product.name" />
          </router-link>
        </template>
        <template v-else>
          <router-link :to="`/${product.slug}`">
            <div class="ribbon" v-if="product.highlight_id">
              <span
                :style="`border-color: ${product.highlight_hex_code}; background: ${product.highlight_hex_code}; background: linear-gradient(${ product.highlight_hex_code } 0%, ${ product.highlight_hex_code } 100%);`">{{
                product.highlight_name }}</span>
            </div>
            <img v-if="product.image" :src="product.image" class="" :alt="product.name" />
            <img v-else :src="`${$imagePath}` + getDefaultImage(product.product_type_id)" class="" :alt="product.name" />
          </router-link>
        </template>
      </div>
      <div class="px-3 pb-3">
        <div class="product-details">
          <div class="product-name mb-1">
            <break-down :key="`${product.id}-${product.breakdown}`" :breakdowns="product.breakdown" :programCode="product.programtype" />
            <span class="d-inline-block highlight-description" v-if="product.highlight_description">
              <Popper :arrow="true" :hover="true" :content="product.highlight_description" style="margin:0; border: none;">
                <i class="fa-solid fa-circle-info info-icon"></i>
              </Popper>
            </span>
            <template v-if="deliveryDate != ''">
              <router-link :to="`/${product.slug}?date=${deliveryDate}`">{{ product.name }}<template v-if="product.programtype == 'fdb' && product.notes">&nbsp;{{ product.notes }}</template></router-link><br />
            </template>
            <template v-else>
              <router-link :to="`/${product.slug}`">{{ product.name }}<template v-if="product.programtype == 'fdb' && product.notes">&nbsp;{{ product.notes }}</template></router-link><br />
            </template>
            <Popper v-if="product.country_id > 0" :arrow="true" :hover="true" :content="product.country_name">
              <div class="text-center is_us_container">
                <span class="us_img_icon d-inline-block">
                  <img :src="`${$imagePath}` + product.country_flag" /></span>
              </div>
            </Popper>
            <Popper v-if="product.state_id > 0" :arrow="true" :hover="true" :content="product.state_name">
              <div class="text-center is_us_container">
                <span class="us_img_icon d-inline-block">
                  <img :src="`${$imagePath}` + product.state_flag" /></span>
              </div>
            </Popper>
          </div>
          <template v-if="isLoggedIn">
            <p>
              <small><i>{{ product.farm_name }}</i></small>
            </p>
            <p>
              <small>
                <span v-if="['mdb', 'lal', 'fdb', 'wsp'].includes(product.programtype)">{{ product.qty }}&nbsp;</span>
                <template v-if="['lal', 'prb', 'wsp'].includes(product.programtype)">
                  {{ product.uom_display ? product.qty > 1 ? product.uom_plural : product.uom_name : '' }}
                </template>
                <template v-else>
                  {{ product.qty > 1 ? product.box_name_plural : product.box_name_singular }}
                </template>
                <span v-if="['mdb', 'fdb'].includes(product.programtype) && product.uom_name != null && product.uom_name != ''">
                  ({{ product.unit_count }} {{ product.uom_display ? product.unit_count > 1 ? product.uom_plural : product.uom_name : '' }})
                </span>
              </small>
            </p>
            <p><small>&nbsp;</small></p>
            <p>
              <b>${{ $filters.numberFormat(product.price_per_stem) }} / {{ product.uom_name }}</b>
              <ShopDiscountPill :discount="product.special" />
            </p>
            <p>
              <small v-if="product.inc_quantity > 1 || ['mdb', 'fdb'].includes(product.programtype)">
                ${{ $filters.numberFormat(product.price_per_unit) }} /
                <template v-if="['lal', 'prb', 'wsp'].includes(product.programtype)">
                  <span v-if="product.inc_quantity > 1">{{ product.inc_quantity }} {{ product.uom_plural }}</span>
                  <span v-else>{{ product.uom_name }}</span>
                </template>
                <template v-else>
                  {{ product.box_name_singular }}
                </template>
              </small>
              <small v-else>&nbsp;</small>
            </p>
          </template>
        </div>
        <div class="d-flex align-items-center justify-content-center my-2">
          <a v-if="!isLoggedIn" :href="'/login'" class="btn btn-theme ml-1">Login</a>
          <template v-else>
            <add-to-cart 
              :max="getMaxQuantity(product)" 
              :min="product.min_qty" 
              :step="product.inc_quantity" 
              :prod="product"
              :deliveryDate="deliveryDate" 
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import AddToCart from "./AddToCart.vue";
import AddToWishlist from "./AddToWishlist.vue";
import BreakDown from "./BreakDown.vue";
import ShopDiscountPill from "./ShopDiscountPill.vue";
import Popper from "vue3-popper";
import { useGetters, useMisc } from "@/composables";

defineProps({
  max: {
    type: Number,
    default: 1,
  },
  product: {
    type: Object,
    required: true,
  },
  deliveryDate: {
    required: true,
  },
});

const { isLoggedIn } = useGetters();
const { getDefaultImage, getMaxQuantity } = useMisc();

// Data
const us_message = ref("US Product");

// Lifecycle
onMounted(() => {
  let site_settings = JSON.parse(localStorage.getItem("site_settings"));
  us_message.value = site_settings.us_flag_message;
});
</script>

<style scoped>
.card {
  border: 1px solid #000;
  padding: 5px;
  margin-right: 10px;
}
.no-overflow {
  overflow: initial;
}
.no-overflow .product-wrapper,
.no-overflow .product-wrapper .product-img {
  overflow: initial !important;
}
.us_img_icon {
  width: 40px;
}
/* .us_img_icon img{width:40px; height: 20px;} */
.is_us_container {
  width: 40px;
  margin: 0px auto;
}
.star-color {
  color: #f37f62;
}
.product-name a {
  text-decoration: none;
}

.product-grid :deep(.cutoff_label) { margin: 0 auto !important;}
@media screen and (max-width: 480px) {
  .product-name a {
    text-decoration: none;
    font-size: 12px;
  }
  .product-name {
    height: 70px !important;
  }

  
}
</style>